import { compact } from "lodash";
import { useState } from "react";
import {
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button, { buttonTheme } from "@components/button/button";
import FormFields from "@components/form/form-fields";
import ToggleChevronIcon from "@components/layout/toggle-chevron-icon";
import DeprecatedSelect from "@components/select/select";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { alphabet, assessmentQuestionCommentOptions } from "@helpers/constants";

import { TalentIndicatorForm } from "./talent-indicator-sidebar";

const TalentIndicatorSidebarIndicator = ({
  talentIndicator,
  canUpdate,
  sectionIndex,
  questionIndex,
  isFirstQuestion,
  isLastQuestion,
  onUpdateQuestion,
  onMoveQuestionUp,
  onMoveQuestionDown,
}: {
  talentIndicator: TalentIndicatorForm;
  canUpdate: boolean;
  sectionIndex: number;
  questionIndex: number;
  isFirstQuestion: boolean;
  isLastQuestion: boolean;
  onUpdateQuestion: (data: TalentIndicatorForm) => void;
  onMoveQuestionUp: () => void;
  onMoveQuestionDown: () => void;
}) => {
  const label = useLabel();
  const [isShowingSettings, setIsShowingSettings] = useState(false);

  return (
    <div className="bg-white border rounded">
      <div className="p-6">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            <div className="py-0.5 px-2 rounded bg-green-100 font-bold">{`${
              alphabet[sectionIndex]
            }${questionIndex + 1}`}</div>
            <div className="font-medium">{talentIndicator.title}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              disabled={!canUpdate || isFirstQuestion}
              icon={TopicflowIconMap.upArrow}
              theme={buttonTheme.iconGray}
              onClick={onMoveQuestionUp}
            />
            <Button
              disabled={!canUpdate || isLastQuestion}
              icon={TopicflowIconMap.downArrow}
              theme={buttonTheme.iconGray}
              onClick={onMoveQuestionDown}
            />
            <ToggleSwitch
              aria-label="Talent indicator toggle"
              disabled={!canUpdate}
              checked={talentIndicator.enabled}
              onChange={(enabled) => {
                onUpdateQuestion({ ...talentIndicator, enabled });
              }}
            />
          </div>
        </div>
      </div>

      {!isEmptyValue(talentIndicator.description) && (
        <div className="p-6 border-t">
          <div className="mt-4">
            <FormFields
              fields={[
                {
                  title: "Description",
                  render: () => (
                    <TextareaWysiwyg
                      editable={false}
                      placeholder="Add decription (optional)"
                      value={talentIndicator.description}
                      deps={[talentIndicator.title]}
                    />
                  ),
                },
              ]}
            />
          </div>
        </div>
      )}

      <div className="p-6 border-t">
        <Button
          theme={buttonTheme.text}
          className="flex items-center gap-2 font-semibold -ml-[10px]"
          onClick={() => setIsShowingSettings(!isShowingSettings)}
        >
          <div>Settings</div>
          <ToggleChevronIcon expanded={isShowingSettings} />
        </Button>

        {isShowingSettings && talentIndicator.enabled && (
          <div className="mt-4">
            <FormFields
              fields={compact([
                !talentIndicator.isManagerIndicator && {
                  title: "Responders",
                  render: () => (
                    <DeprecatedSelect
                      disabled={!canUpdate}
                      value={talentIndicator.responses}
                      options={compact([
                        {
                          label: "Both Manager and Subject",
                          description: "Everyone answers this question",
                          value: AssessmentQuestionResponses.Both,
                        },
                        talentIndicator.responseVisibility !==
                          AssessmentQuestionResponseVisibility.HiddenFromSubject && {
                          label: `Self ${label("review")} only`,
                          description: `Only the subject of the ${label(
                            "review"
                          )} answers this question`,
                          value: AssessmentQuestionResponses.SelfAssessmentOnly,
                        },
                        {
                          label: "Manager Only",
                          description:
                            "Only the manager of the subject answers this question",
                          value:
                            AssessmentQuestionResponses.ExcludeSelfAssessment,
                        },
                      ])}
                      onChange={(opt) => {
                        onUpdateQuestion({
                          ...talentIndicator,
                          responses: opt.value,
                        });
                      }}
                      aria-label="Responses type select"
                    />
                  ),
                },
                {
                  title: "Response visibility",
                  render: () => (
                    <DeprecatedSelect
                      disabled={
                        !canUpdate ||
                        talentIndicator.responses ===
                          AssessmentQuestionResponses.SelfAssessmentOnly
                      }
                      value={talentIndicator.responseVisibility}
                      options={compact([
                        {
                          label: "Everyone",
                          description: `Responses to this question are visible by anyone who can access the ${label(
                            "review"
                          )}`,
                          value: AssessmentQuestionResponseVisibility.All,
                        },
                        talentIndicator.responses !==
                          AssessmentQuestionResponses.SelfAssessmentOnly && {
                          label: "Hide from subject",
                          description: `Responses to this question, except for their own, are hidden from the subject of the ${label(
                            "review"
                          )}`,
                          value:
                            AssessmentQuestionResponseVisibility.HiddenFromSubject,
                        },
                      ])}
                      onChange={(opt) => {
                        onUpdateQuestion({
                          ...talentIndicator,
                          responseVisibility: opt.value,
                        });
                      }}
                      aria-label="Response visibility type select"
                    />
                  ),
                },
                {
                  title: "Comments",
                  render: () => (
                    <DeprecatedSelect
                      disabled={!canUpdate}
                      value={talentIndicator.comment}
                      options={assessmentQuestionCommentOptions}
                      onChange={(opt) => {
                        onUpdateQuestion({
                          ...talentIndicator,
                          comment: opt.value,
                        });
                      }}
                      aria-label="Mandatory comment select"
                    />
                  ),
                },
              ])}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentIndicatorSidebarIndicator;
